import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Users, DollarSign, Clock, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import adminClient from '@/lib/api/adminClient';
import { formatCurrency, formatDate } from '@/lib/utils';

const STATUS_VARIANT: Record<string, 'success' | 'destructive' | 'secondary' | 'warning' | 'outline'> = {
  paid: 'success',
  confirmed: 'warning',
  pending: 'secondary',
  cancelled: 'outline',
};

const STATUS_LABEL: Record<string, string> = {
  pending: '待確認',
  confirmed: '已確認',
  paid: '已結算',
  cancelled: '已取消',
};

const TYPE_LABEL: Record<string, string> = {
  platform_first: '首月 (20%)',
  platform_recurring: '持續 (10%)',
  strategy: '策略銷售 (3%)',
};

export function ReferralPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'referrals', page, statusFilter],
    queryFn: () =>
      adminClient.get('/admin/referrals', {
        params: {
          page,
          per_page: 20,
          status: statusFilter !== 'all' ? statusFilter : undefined,
        },
      }).then((r) => r.data),
  });

  const items: any[] = data?.items || [];

  // Summary stats from current data
  const totalCommission = items.reduce((s: number, r: any) => s + r.commission_amount, 0);
  const pendingCount = items.filter((r: any) => r.status === 'pending').length;
  const paidCount = items.filter((r: any) => r.status === 'paid').length;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">推薦分潤管理</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-xs text-muted-foreground">總筆數</p>
              <p className="text-xl font-bold">{data?.total ?? 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-xs text-muted-foreground">本頁分潤合計</p>
              <p className="text-xl font-bold">{formatCurrency(totalCommission)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-8 h-8 text-amber-500" />
            <div>
              <p className="text-xs text-muted-foreground">待確認</p>
              <p className="text-xl font-bold">{pendingCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
            <div>
              <p className="text-xs text-muted-foreground">已結算</p>
              <p className="text-xl font-bold">{paidCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
        <SelectTrigger className="w-[150px]"><SelectValue placeholder="篩選狀態" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">所有狀態</SelectItem>
          <SelectItem value="pending">待確認</SelectItem>
          <SelectItem value="confirmed">已確認</SelectItem>
          <SelectItem value="paid">已結算</SelectItem>
          <SelectItem value="cancelled">已取消</SelectItem>
        </SelectContent>
      </Select>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>推薦人</TableHead>
                  <TableHead>被推薦人</TableHead>
                  <TableHead>類型</TableHead>
                  <TableHead>原始金額</TableHead>
                  <TableHead>分潤比例</TableHead>
                  <TableHead>分潤金額</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead>建立日期</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-sm">{r.referrer_email ?? `uid:${r.referrer_id}`}</TableCell>
                    <TableCell className="text-sm">{r.referred_email ?? `uid:${r.referred_id}`}</TableCell>
                    <TableCell>
                      <span className="text-xs">{TYPE_LABEL[r.commission_type] ?? r.commission_type}</span>
                    </TableCell>
                    <TableCell className="text-sm">{formatCurrency(r.source_amount)}</TableCell>
                    <TableCell className="text-sm">{(r.commission_rate * 100).toFixed(0)}%</TableCell>
                    <TableCell className="font-medium text-green-600">{formatCurrency(r.commission_amount)}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[r.status] ?? 'secondary'}>
                        {STATUS_LABEL[r.status] ?? r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(r.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total > 20 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>上一頁</Button>
          <span className="text-sm text-muted-foreground">第 {page} 頁，共 {Math.ceil(data.total / 20)} 頁（{data.total} 筆）</span>
          <Button variant="outline" size="sm" disabled={page >= Math.ceil(data.total / 20)} onClick={() => setPage(page + 1)}>下一頁</Button>
        </div>
      )}
    </div>
  );
}
